import * as React from 'react';

import { Button, buttonVariants } from './components/ui/button.js';
import { TOUCH_LAYOUT_CLASSES } from './lib/touch-layout-classes.js';
import { useDisabledTooltip } from './lib/use-disabled-tooltip.js';
import { cn } from './lib/utils.js';

// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };

// Applied here rather than in components/ui/button.tsx, which is shadcn CLI-managed -- editing it
// would be overwritten by the next `shadcn add`/update.
const CURSOR_CLASSES =
  'cursor-pointer disabled:cursor-not-allowed aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

type ShadcnVariant = 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link';
type CustomVariant =
  'warning' | 'success' | 'success-solid' | 'destructive-outline' | 'destructive-ghost';

const DESTRUCTIVE_TEXT_CLASSES = 'text-destructive hover:bg-destructive/10 hover:text-destructive';

const CUSTOM_VARIANTS: Record<CustomVariant, { base: ShadcnVariant; classes: string }> = {
  warning: {
    base: 'default',
    classes:
      'border-amber-600/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:border-amber-400/40 dark:bg-amber-500/20 dark:text-amber-300 dark:hover:bg-amber-500/30',
  },
  success: {
    base: 'default',
    classes:
      'border-green-600/40 bg-green-500/10 text-green-700 hover:bg-green-500/20 dark:border-green-400/40 dark:bg-green-500/20 dark:text-green-300 dark:hover:bg-green-500/30',
  },
  'success-solid': {
    base: 'default',
    classes:
      'bg-green-600 text-white hover:bg-green-700 dark:bg-green-600 dark:text-white dark:hover:bg-green-500',
  },
  'destructive-outline': { base: 'outline', classes: DESTRUCTIVE_TEXT_CLASSES },
  'destructive-ghost': { base: 'ghost', classes: DESTRUCTIVE_TEXT_CLASSES },
};

type ShadcnSize = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg';
/** `touch` is a 44px target for a picker's tablet or phone, built on `lg`. */
export type ButtonSize = ShadcnSize | 'touch';
export interface DimeButtonProps
  extends Omit<React.ComponentProps<'button'>, 'ref'>, React.RefAttributes<HTMLButtonElement> {
  variant?: ShadcnVariant | CustomVariant;
  size?: ButtonSize;
  asChild?: boolean;
  // Shown in a tooltip when the button is disabled — e.g. explaining a missing permission.
  // A disabled button has pointer-events-none and is excluded from the tab order, so the
  // tooltip trigger can't live on the button itself; DimeButton wraps in a non-disabled span
  // that Radix can attach hover/focus listeners to instead.
  disabledTooltip?: string;
  // Pass true when `className` includes `w-full`, so the disabled-tooltip wrapper stretches to match.
  disabledTooltipFullWidth?: boolean;
}

export const DimeButton = React.forwardRef<HTMLButtonElement, Readonly<DimeButtonProps>>(
  function DimeButton(
    {
      variant = 'default',
      size = 'default',
      className,
      disabledTooltip,
      disabledTooltipFullWidth,
      disabled,
      ...props
    },
    ref,
  ) {
    const customVariant =
      variant in CUSTOM_VARIANTS ? CUSTOM_VARIANTS[variant as CustomVariant] : null;

    const touch = size === 'touch';

    const button = (
      <Button
        ref={ref}
        variant={customVariant ? customVariant.base : (variant as ShadcnVariant)}
        size={touch ? 'lg' : size}
        disabled={disabled}
        className={cn(
          CURSOR_CLASSES,
          customVariant?.classes,
          touch && TOUCH_LAYOUT_CLASSES.control,
          className,
        )}
        {...props}
      />
    );

    return useDisabledTooltip(
      { disabled, tooltip: disabledTooltip, fullWidth: disabledTooltipFullWidth },
      button,
    );
  },
);

DimeButton.displayName = 'DimeButton';
